import type { ResidentItem } from '@lmc/ts-common'
import { CollapsibleText } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import {
  ADJUSTMENT_SUBCATEGORIES_BY_CATEGORY,
  type AdjustmentCategoryCode,
  AdjustmentCategoryDescriptionEnum,
  ImpairmentDescriptionEnum
} from '@lmc/validations'
import type { DescriptionsItemType } from 'antd/es/descriptions'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ChildrenContainer } from '../styledComponents'

type ReasonableAdjustmentContentProps = {
  adjustmentSubcategory?: Partial<Record<string, string[]>>
  adjustmentCategoryCodes: string[] | undefined
  details: string | undefined
  impairmentLabelsArray: string[] | undefined
}

const DESCRIPTION_LINES_FOR_TOGGLE = 3

const ReasonableAdjustmentContent = ({
  adjustmentCategoryCodes,
  adjustmentSubcategory,
  details,
  impairmentLabelsArray
}: ReasonableAdjustmentContentProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const hasCategories =
    adjustmentCategoryCodes && adjustmentCategoryCodes.length > 0
  const hasDetails = Boolean(details)
  const hasImpairments =
    impairmentLabelsArray && impairmentLabelsArray.length > 0

  return (
    <>
      {hasCategories && (
        <Section>
          {adjustmentCategoryCodes.map((catCode) => {
            const subcodes =
              adjustmentSubcategory?.[catCode]?.filter(Boolean) ?? []
            return (
              <CategoryGroup key={catCode}>
                <CategoryName>
                  {AdjustmentCategoryDescriptionEnum[
                    catCode as AdjustmentCategoryCode
                  ] ?? catCode}
                </CategoryName>
                {subcodes.length > 0 && (
                  <BulletList>
                    {subcodes.map((code) => (
                      <li key={code}>
                        <Text>
                          {ADJUSTMENT_SUBCATEGORIES_BY_CATEGORY[
                            catCode as AdjustmentCategoryCode
                          ].find((e) => e.snomedCtCode === code)?.displayTerm ??
                            code}
                        </Text>
                      </li>
                    ))}
                  </BulletList>
                )}
              </CategoryGroup>
            )
          })}
        </Section>
      )}
      {hasDetails && (
        <Section>
          <CategoryGroup>
            <CategoryName>
              {t(supConstants.REASONABLE_ADJUSTMENT_DETAILS)}
            </CategoryName>
            <CollapsibleText lines={DESCRIPTION_LINES_FOR_TOGGLE}>
              {details}
            </CollapsibleText>
          </CategoryGroup>
        </Section>
      )}
      {hasImpairments && (
        <Section>
          <CategoryGroup>
            <CategoryName>
              {t(supConstants.REASONABLE_ADJUSTMENT_IMPAIRMENT)}
            </CategoryName>
            <BulletList>
              {impairmentLabelsArray.map((label, idx) => (
                <li key={idx}>
                  <Text>{label}</Text>
                </li>
              ))}
            </BulletList>
          </CategoryGroup>
        </Section>
      )}
    </>
  )
}

export const getReasonableAdjustmentsViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  if (!resident.reasonableAdjustments?.length) return []

  return [
    {
      key: 'reasonable-adjustments',
      label: <Text>{t(supConstants.REASONABLE_ADJUSTMENTS)}</Text>,
      labelStyle: { verticalAlign: 'top' },
      children: (
        <ChildrenContainer>
          {resident.reasonableAdjustments.map((adjustment, index) => {
            const impairmentLabelsArray = adjustment.impairment?.map(
              (code) =>
                ImpairmentDescriptionEnum[
                  code as keyof typeof ImpairmentDescriptionEnum
                ] || code
            )
            return (
              <div key={index}>
                {index > 0 && <AdjustmentDivider />}
                <ReasonableAdjustmentContent
                  adjustmentCategoryCodes={adjustment.adjustmentCategory}
                  adjustmentSubcategory={
                    (adjustment as any).adjustmentSubcategory
                  }
                  details={adjustment.details}
                  impairmentLabelsArray={impairmentLabelsArray}
                />
              </div>
            )
          })}
        </ChildrenContainer>
      )
    }
  ]
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  & + & {
    padding-top: 8px;
  }
`

const AdjustmentDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.neutral.color8};
  margin: 12px 0;
`

const CategoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  & + & {
    padding-top: 8px;
  }
`

const CategoryName = styled(Text)`
  font-weight: 600;
`

const BulletList = styled.ul`
  margin: 0;
  padding-left: 20px;
`
